import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { paths } from '../../app/routes'
import { useToast } from '../../components/feedback/toastContext'
import { CameraViewfinder } from '../../components/scanner/CameraViewfinder'
import { ScanSimulator } from '../../components/scanner/ScanSimulator'
import { useDeviceSettings } from '../../feedback/settings'
import { faNumber } from '../../lib/faNumbers'
import { useBasketCount } from '../../outbox/useOutbox'
import { queueBasketAssign } from '../../outbox/operations'
import { useBarcodeScanner } from '../../scanner/useBarcodeScanner'
import { useTorch } from '../../scanner/useTorch'
import { manualRouteFor } from '../Manual/manualTarget'
import type { AssignResultState } from './assignState'
import styles from './AssignScreen.module.css'

const PALLET_CODE = /^PLT-[A-Za-z0-9-]+$/

export function AssignScreen() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { show } = useToast()
  const { settings } = useDeviceSettings()
  const basketCount = useBasketCount()
  const assigning = useRef(false)
  const consumedManual = useRef<string | null>(null)

  const assignTo = useCallback(
    async (palletCode: string) => {
      if (assigning.current) {
        return
      }

      assigning.current = true

      try {
        const assignment = await queueBasketAssign(palletCode)

        if (assignment.operations.length === 0) {
          show('سبد خالی است', 'warn')
          assigning.current = false
          return
        }

        const resultState: AssignResultState = {
          palletCode,
          opIds: assignment.operations.map((operation) => operation.id),
          basketEntries: assignment.entries,
        }

        navigate(paths.assignResult, { state: resultState })
      } catch {
        assigning.current = false
        show('عملیات انجام نشد', 'err')
      }
    },
    [navigate, show],
  )

  const onDetect = useCallback(
    (value: string) => {
      const code = value.trim().toUpperCase()

      if (!PALLET_CODE.test(code)) {
        show('این بارکد پالت نیست. ابتدا بارکد پالت را اسکن کنید.', 'warn')
        return
      }

      void assignTo(code)
    },
    [assignTo, show],
  )

  const scanner = useBarcodeScanner({
    enabled: true,
    onDetect,
    region: { width: 0.8, height: 0.45 },
  })
  useTorch(scanner.track, settings.torch)

  const manualPallet = readManualPallet(state)

  useEffect(() => {
    if (manualPallet === null || consumedManual.current === manualPallet) {
      return
    }

    consumedManual.current = manualPallet
    void assignTo(manualPallet)
    navigate('.', { replace: true, state: null })
  }, [assignTo, manualPallet, navigate])

  const openManual = () => {
    const route = manualRouteFor({ kind: 'assignPallet' })
    navigate(route.to, { state: route.state })
  }

  return (
    <div className={styles.screen}>
      <div className={styles.summary}>
        <div className={styles.summaryCopy}>هر سریال با درخواست جداگانه ثبت می‌شود</div>
        <div className={styles.summaryCount}>
          {faNumber(basketCount)} قلم آماده تخصیص
        </div>
      </div>

      <CameraViewfinder
        size="assign"
        badgeLabel="دوربین فعال"
        hint="بارکد پالت مقصد را اسکن کنید"
        status={scanner.status}
        error={scanner.error}
        videoRef={scanner.videoRef}
        onActivate={scanner.retry}
        showBadge={false}
      />

      {import.meta.env.DEV && <ScanSimulator onScan={onDetect} />}

      <button type="button" className={styles.manual} onClick={openManual}>
        ورود دستی کد پالت
      </button>
    </div>
  )
}

function readManualPallet(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) {
    return null
  }

  const candidate = value as { manualPallet?: unknown }

  if (typeof candidate.manualPallet !== 'string' || !PALLET_CODE.test(candidate.manualPallet)) {
    return null
  }

  return candidate.manualPallet
}
